import { useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { configurationApi } from "~/api";
import type { Status, StatusCode } from "~/types/api";
import { useCreateList, useUpdateList, useDeleteList } from "./use-list-mutations";
import { useLists } from "./use-lists";

type Props = {
  lists: ReturnType<typeof useLists>["lists"];
  isLoading: boolean;
  isError: boolean;
  workflow: null;
  customer: null;
  allWorkflows: ReturnType<typeof useLists>["allWorkflows"];
  allCustomers: ReturnType<typeof useLists>["allCustomers"];
  workflowsLoading: boolean;
  customersLoading: boolean;
  createList: (
    data: { name: string; workflowId: number },
    statuses?: StatusCode[]
  ) => Promise<unknown>;
  updateList: (
    listId: number,
    data: { name: string; workflowId: number; originalName?: string },
    statusCodes?: StatusCode[],
    originalStatusCodes?: StatusCode[]
  ) => Promise<void>;
  deleteList: (
    listId: number,
    listWorkflowId: number,
    options?: { onSuccess?: () => void }
  ) => Promise<void>;
  getWorkflowStatuses: (targetWorkflowId: number | null) => UseQueryResult<Status[], unknown>;
  getWorkflowListStatuses: (
    targetWorkflowId: number | null,
    targetListId: number | null
  ) => UseQueryResult<StatusCode[], unknown>;
  refetch: () => void;
};

export const useListData = (): Props => {
  const queryClient = useQueryClient();

  const {
    lists,
    isLoading,
    isError,
    allWorkflows,
    allCustomers,
    workflowsLoading,
    customersLoading,
  } = useLists();

  const createListMutation = useCreateList();
  const updateListMutation = useUpdateList();
  const deleteListMutation = useDeleteList();

  const createList = async (
    data: { name: string; workflowId: number },
    statusCodes?: StatusCode[]
  ) => {
    return await createListMutation.mutateAsync({
      name: data.name,
      workflowId: data.workflowId,
      statusCodes,
    });
  };

  const updateList = async (
    listId: number,
    data: { name: string; workflowId: number; originalName?: string },
    statusCodes?: StatusCode[],
    originalStatusCodes?: StatusCode[]
  ) => {
    await updateListMutation.mutateAsync({
      listId,
      workflowId: data.workflowId,
      name: data.name,
      originalName: data.originalName,
      statusCodes,
      originalStatusCodes,
    });
  };

  const deleteList = async (
    listId: number,
    workflowId: number,
    options?: { onSuccess?: () => void }
  ) => {
    await deleteListMutation.mutateAsync({ listId, workflowId });
    options?.onSuccess?.();
  };

  const getWorkflowStatuses = (targetWorkflowId: number | null) => {
    return configurationApi.useQuery(
      "get",
      "/api/workflow/{workflowId}/Status",
      {
        params: { path: { workflowId: targetWorkflowId! } },
      },
      {
        enabled: !!targetWorkflowId && targetWorkflowId > 0,
        meta: { errorTitle: "Failed to load lists" },
      }
    );
  };

  const getWorkflowListStatuses = (
    targetWorkflowId: number | null,
    targetListId: number | null
  ) => {
    return configurationApi.useQuery(
      "get",
      "/api/workflow/{workflowId}/list/{listId}/status",
      {
        params: { path: { workflowId: targetWorkflowId!, listId: targetListId! } },
      },
      {
        enabled: !!targetWorkflowId && targetWorkflowId > 0 && !!targetListId && targetListId > 0,
        meta: { errorTitle: "Failed to load lists" },
      }
    );
  };

  const refetch = () => {
    queryClient.invalidateQueries({
      queryKey: configurationApi.queryOptions("get", "/api/List").queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: configurationApi.queryOptions("get", "/api/Workflow").queryKey,
    });
    queryClient.invalidateQueries({
      queryKey: configurationApi.queryOptions("get", "/api/Customer").queryKey,
    });
  };

  return {
    lists,
    isLoading,
    isError,
    workflow: null,
    customer: null,
    allWorkflows,
    allCustomers,
    workflowsLoading,
    customersLoading,
    createList,
    updateList,
    deleteList,
    getWorkflowStatuses,
    getWorkflowListStatuses,
    refetch,
  };
};
